import { BrandMark } from "@cntrl/ui";
import { bandRoleLabel } from "../../app/hooks/useBandTeam.js";
import { roleLabelFrom, useSeatRoles } from "../../app/hooks/useSeatRoles.js";
import styles from "./RoleLabel.module.scss";

/**
 * Which ink the label is drawn in. Named for the INK, not the surface it sits on:
 *
 * - `dark` (the default) — the label inherits the surrounding text colour, so a row sub-line stays
 *   muted and a heading stays full ink, and only the CNTRL mark is pinned, to crimson.
 * - `light` — the whole label, mark included, is paper. Use it on ink/scrim surfaces instead of
 *   giving the call site its own `color: var(--ac-color-paper)`.
 */
export type RoleLabelVariant = "dark" | "light";

export interface RoleLabelProps {
  /**
   * The seat's granted ROLE key (e.g. `band_member_av`), which decides the AV mark. OPTIONAL: when omitted,
   * it is recovered from {@link titleKey} via the catalog's title→role map — so a surface that only holds a
   * title (a message-author line, a bus roster row) still draws the mark by passing `titleKey` alone.
   */
  role?: string | null;
  /**
   * The seat's TITLE — what this person is CALLED — resolved against the server catalog and preferred over
   * {@link role} for the words. Pass it wherever the DTO carries one.
   *
   * ⚠️ A JOB LABEL IS RESOLVED FROM THE TITLE, NEVER THE ROLE. 70 titles collapse onto 22 roles, so a label
   * recovered from a role names the wrong job: all 11 production titles grant `production_crew`, and an FOH
   * Engineer rendered from the role reads "Production Crew". The role still decides the AV mark below, which
   * is a permission fact rather than a job name — which is exactly why both are props.
   */
  titleKey?: string | null;
  /** The OWNER seat reads "Artist" + the mark (PO, 2026-08-20) — ownership outranks the role slug, and
   * the old standalone OWNER badge is retired with it. A non-owner `band_member_av` stays "Band Member". */
  owner?: boolean;
  /**
   * Spell out the full-access qualifier — "Band Member (Full Access)" instead of "Band Member" + mark alone
   * (ART-171, PO). Pass it ONLY inside a role PICKER, where you are choosing between the full-access and
   * limited "Band Member" titles and the mark is too quiet to disambiguate. Everywhere a role is DISPLAYED
   * as a fact — a crew row, a profile pill, a message-author line — leave it off: the crimson mark IS the
   * "full access" signal, so the words repeating it are noise. Inert on every other seat, so a picker can
   * pass it unconditionally across all its options.
   */
  spellFullAccess?: boolean;
  variant?: RoleLabelVariant;
  className?: string;
}

/**
 * The one way a seat role reaches the screen. Two rules the raw label can't carry:
 *
 * - `band_member_av` (the full-access seat) reads "Band Member" followed by the CNTRL mark — never
 *   "Band Member Av", and never a parenthetical the mark already says (ART-171). The ONE exception is a
 *   role PICKER, which passes {@link RoleLabelProps.spellFullAccess} to render "Band Member (Full Access)":
 *   there the mark is too quiet to separate the full-access "Band Member" from the limited one. The mark is
 *   ALWAYS crimson (`--ac-color-accent`), on every surface, whatever colour the surrounding label is
 *   (PO, 2026-08-20); that is pinned in the stylesheet, so route any new AV surface through this component
 *   rather than rendering `<BrandMark>` next to a role string yourself.
 * - every other slug goes through {@link bandRoleLabel}, which keeps the acronym seats correct
 *   (FOH Engineer, RF Tech, PA Tech 1/2) instead of title-casing them into "Foh Engineer".
 *
 * The `<select>` in the crew card still uses the plain text label — an `<option>` can't hold a glyph.
 */
export function RoleLabel({
  role,
  titleKey,
  owner = false,
  spellFullAccess = false,
  variant = "dark",
  className,
}: RoleLabelProps) {
  const seatRoles = useSeatRoles();
  const tone = [variant === "light" ? styles.light : styles.dark, className].filter(Boolean).join(" ");

  // The catalog is person-scoped with staleTime: Infinity, so this is one shared fetch for every seat on
  // screen. bandRoleLabel stays the fallback for a row carrying no title and for the first paint before the
  // catalog lands — a seat must never reach the screen as a raw snake_case key.
  const catalog = seatRoles.data?.bandRoles;
  const fromTitle = titleKey ? roleLabelFrom(catalog, titleKey) : "";

  // The AV MARK and the WORDS are two different facts, and conflating them cost the picker. The mark means
  // full access — a property of the ROLE — while the words name the job, which comes from the TITLE. This
  // used to return "Band Member" + mark for anything whose role was band_member_av, which is right for a row
  // and wrong for a list: `artist_owner` and `band_member_full` are two titles on that one role, so both
  // options rendered identically and the picker offered the same thing twice.
  //
  // The role can be RECOVERED from the title (the catalog's title→role map) for a caller that only has a
  // titleKey — that draws the mark from a message author or a bus roster row, which never carry the role key.
  // The recovery is role-only: the WORDS still come from the title, never from the role (the ~70-titles rule).
  const effectiveRole = role ?? (titleKey ? catalog?.find((o) => o.role === titleKey)?.roleKey ?? null : null);
  const isAv = effectiveRole === "band_member_av";
  const showMark = owner || isAv;
  const words = owner ? (fromTitle || "Artist") : (fromTitle || (isAv ? "Band Member" : bandRoleLabel(effectiveRole)));

  // ART-171: the full-access title's WORDS are just "Band Member" (its stored label, seed 861) — the crimson
  // mark IS the "full access" signal. A role PICKER opts into spelling it out, because there the mark is too
  // quiet to tell the full-access "Band Member" from the limited one. Only the non-owner AV seat qualifies
  // (the owner reads "Artist"), so a picker can pass the flag across every option and it lands on the one.
  const isFullAccessSeat = !owner && isAv;
  // The qualifier trails the MARK, not the words: the PO's form is "Band Member [red logo] (Full Access)"
  // (ART-171). `.root` is an inline-flex with a gap, so the leading space is collapsed visually at the
  // flex-item boundary while staying in textContent — the option's accessible name and what the tests read.
  const qualifier = spellFullAccess && isFullAccessSeat ? " (Full Access)" : "";

  if (showMark) {
    // When the qualifier is shown its sr-only twin would double it, so drop it there; the owner's ", owner"
    // and the plain full-access ", full access" stay.
    const srSuffix = owner ? ", owner" : qualifier ? "" : ", full access";
    return (
      <span className={`${styles.root} ${tone}`}>
        {words}
        {/* The stylesheet scales the mark to the adjacent text (0.9em); size is only the pre-CSS fallback.
            Decorative: the sr-only twin (or the visible "(Full Access)") carries the meaning, so the mark is
            not announced as "CNTRL mark" beside every seat. */}
        <BrandMark size={10} className={styles.mark} decorative />
        {/* ART-171: the parenthetical trails the MARK — "Band Member [logo] (Full Access)" — as its own node. */}
        {qualifier}
        {srSuffix && <span className={styles.srOnly}>{srSuffix}</span>}
      </span>
    );
  }
  return <span className={tone}>{words}</span>;
}
